import React, { useRef, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable } from '../lib/react-native-compat';

// The Schedule & Baseline / Baseline & Weekly upload card: drag-and-drop or
// pick a file, show the active import with its row count and uploader, and
// offer a Remove that falls back to the previous upload. Extracted from
// projectSchedule.js so both pages share one implementation.

export const SCHEDULE_EXT = ['xer', 'xml', 'xlsx', 'xlsm', 'xls', 'csv'];
export const BASELINE_EXT = ['xlsx', 'xlsm', 'xls', 'csv'];

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

// Row counts and quantities read as whole numbers here — fNum's default two
// decimals turns '2 rows' into '2.00 rows'.
export const intFmt = (v) => Math.round(num(v)).toLocaleString();

export const readFileBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
});

export const UploadCard = ({ title, hint, accept, exts, busy, activeImport, onFile, onDelete, error, notice }) => {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    // Remove confirms in place rather than through window.confirm. A browser
    // that has been asked to stop showing dialogs — Chrome offers exactly that
    // after a few of them — returns false from confirm() forever after, which
    // made Remove look dead with nothing in the console to explain it.
    const [confirming, setConfirming] = useState(false);

    const take = (file) => {
        if (!file) return;
        const ext = String(file.name).toLowerCase().split('.').pop();
        if (!exts.includes(ext)) {
            onFile(file, `That looks like a .${ext} file — this box takes ${exts.map((e) => `.${e}`).join(', ')}.`);
            return;
        }
        onFile(file);
    };

    return (
        <View
            style={{
                flex: 1,
                minWidth: 260,
                borderRadius: 10,
                border: `1px ${dragging ? 'solid #1D4ED8' : 'dashed #CBD5E1'}`,
                backgroundColor: dragging ? '#EFF6FF' : '#F8FAFC',
                padding: 12,
            }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                take(e.dataTransfer?.files?.[0]);
            }}
        >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', columnGap: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>{title}</Text>
                <Pressable
                    onPress={() => inputRef.current?.click()}
                    style={{ borderRadius: 8, backgroundColor: busy ? '#94A3B8' : '#1D4ED8', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, cursor: busy ? 'default' : 'pointer' }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>
                        {busy ? 'Uploading…' : 'Choose file'}
                    </Text>
                </Pressable>
            </View>
            <Text style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>{hint}</Text>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(e) => {
                    take(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            {activeImport ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, marginTop: 8, borderTop: '1px solid #E2E8F0', paddingTop: 8 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                        <Text numberOfLines={1} style={{ fontSize: 12, fontWeight: '600', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {activeImport.File_Name || '(no file name)'}
                        </Text>
                        <Text style={{ fontSize: 10.5, color: '#64748B', marginTop: 1 }}>
                            {[
                                `${intFmt(activeImport.Row_Count || 0)} rows`,
                                activeImport.Source,
                                activeImport.Uploaded_By,
                                activeImport.Uploaded_At ? moment.utc(activeImport.Uploaded_At).format('M/D/YY h:mm A') : null,
                            ].filter(Boolean).join(' · ')}
                        </Text>
                    </View>
                    {confirming ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 6 }}>
                            <Text style={{ fontSize: 10.5, color: '#B91C1C' }}>
                                Remove it? The previous upload becomes active again.
                            </Text>
                            <Pressable
                                onPress={() => { setConfirming(false); onDelete(activeImport.Import_ID); }}
                                style={{ borderRadius: 8, backgroundColor: '#B91C1C', paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>Remove</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => setConfirming(false)}
                                style={{ borderRadius: 8, border: '1px solid #CBD5E1', backgroundColor: '#fff', paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 11, fontWeight: '700', color: '#475569' }}>Cancel</Text>
                            </Pressable>
                        </View>
                    ) : (
                        <Pressable
                            onPress={() => setConfirming(true)}
                            title="Remove this upload"
                            style={{ borderRadius: 8, border: '1px solid #FECACA', backgroundColor: '#FEF2F2', paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}
                        >
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#B91C1C' }}>Remove</Text>
                        </Pressable>
                    )}
                </View>
            ) : (
                <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 8, borderTop: '1px solid #E2E8F0', paddingTop: 8 }}>
                    Nothing uploaded for this job yet — drop a file here.
                </Text>
            )}
            {notice ? <Text style={{ fontSize: 11, color: '#B45309', marginTop: 6 }}>{notice}</Text> : null}
            {error ? <Text style={{ fontSize: 11, color: '#B91C1C', marginTop: 6 }}>{error}</Text> : null}
        </View>
    );
};

export default UploadCard;
